import React, { useState } from 'react';
import { useDivisionApiKeys, useDeleteApiKey } from '../../hooks/use-api-keys';
import { ApiKey } from '../../types';
import { Button } from '../ui/button';
import { formatDate } from '../../utils';

interface ApiKeyTableProps {
  divisionId: string;
  onCreateApiKey: () => void;
}

export function ApiKeyTable({ divisionId, onCreateApiKey }: ApiKeyTableProps) {
  const { data: apiKeysResponse, isLoading, error } = useDivisionApiKeys(divisionId);
  const deleteApiKey = useDeleteApiKey(divisionId);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);

  const apiKeys = apiKeysResponse?.apiKeys || [];

  const handleDeleteKey = async (apiKey: ApiKey) => {
    if (!window.confirm(`Are you sure you want to delete the API key "${apiKey.name}"? This action cannot be undone and will invalidate the key.`)) {
      return;
    }

    setDeletingKeyId(apiKey.id);
    try {
      await deleteApiKey.mutateAsync(apiKey.id);
    } catch (error) {
      console.error('Failed to delete API key:', error);
    } finally {
      setDeletingKeyId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex">
          <div className="text-red-400">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading API keys
            </h3>
            <div className="mt-2 text-sm text-red-700 dark:text-red-400">
              <p>Failed to load API keys for this division. Please try refreshing the page.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (apiKeys.length === 0) {
    return (
      <div className="text-center py-8 text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-900 rounded-lg border">
        <div className="mb-4">
          <svg className="mx-auto h-12 w-12 text-secondary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">No API keys found</h3>
        <p className="text-secondary-600 dark:text-secondary-400 mb-4">
          This division doesn't have any API keys yet. Create one to allow external systems to submit orders.
        </p>
        <Button onClick={onCreateApiKey}>Create API Key</Button>
      </div>
    );
  }

  return (
    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
      <table className="min-w-full divide-y divide-secondary-300">
        <thead className="bg-secondary-50 dark:bg-secondary-900">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              Name
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              Description
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              Created
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              Expires
            </th>
            <th scope="col" className="relative px-6 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200">
          {apiKeys.map((apiKey) => (
            <tr key={apiKey.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-700">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-8 w-8">
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <svg className="h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-secondary-900 dark:text-white">
                      {apiKey.name}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-secondary-500 dark:text-secondary-400">
                <div className="max-w-xs truncate" title={apiKey.description || ''}>
                  {apiKey.description || '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {apiKey.active ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800">
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800">
                    Inactive
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                {formatDate(apiKey.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                {apiKey.expiresAt ? formatDate(apiKey.expiresAt) : 'Never'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteKey(apiKey)}
                  disabled={deletingKeyId === apiKey.id}
                  className="text-red-600 dark:text-red-400 hover:text-red-900"
                >
                  {deletingKeyId === apiKey.id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                  ) : (
                    'Delete'
                  )}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
